import React, {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	memo,
} from "react";
import { useTranslation } from "react-i18next";
import { isUndefined, isString } from "lodash";
import DockLayout, { LayoutBase, TabBase, TabData } from "rc-dock";

import {
	useTypedDispatch,
	useTypedSelector,
} from "../../../../../../redux/store";
import { ModalInterface } from "../../../../../../redux/constants/OrdersPage";
import widgets from "../../../../../../redux/reducers/widgets";
import WidgetRouter from "../../../../../../components/OrderPageWidgets/WidgetRouter";
import { PerfProfiler } from "../../../../../../tests";
import { StyledRow } from "../../../../../../components/common";

import SubscribeProvider from "./components/SubscribeProvider";
import Root from "./components/Root";
import { defaultTab, defaultTabs, groups } from "./data";

const MainOrderPage: React.FC<any> = () => {
	const dispatch = useTypedDispatch();
	const { t } = useTranslation();

	const modals = useTypedSelector((state) => state.ordersPageReducer.modals);
	const savedLayout = useTypedSelector((state) => state.widgets.layout);
	const { lastRead } = useTypedSelector((state) => state.orders.chats.chat);
	const taxiServiceData = useTypedSelector(
		(state) => state.taxiServices.models,
	);
	const settingsLanguage = useTypedSelector(
		(state) => state.session.language,
	);

	const dockLayoutRef = useRef<DockLayout | null>(null);
	const refTabItem = useRef<HTMLDivElement | null>(null);
	const layoutPersistTimer = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const pendingLayout = useRef<LayoutBase | null>(null);

	const contents = useMemo(
		() =>
			modals
				.filter((item) => item.visible)
				.reduce((accumulator, item: ModalInterface) => {
					if (item.type === "parking") {
						accumulator[item.type] = (
							<WidgetRouter
								key={item.type}
								{...item}
								taxiServiceData={taxiServiceData}
							/>
						);
					} else {
						accumulator[item.type] = (
							<WidgetRouter key={item.type} {...item} />
						);
					}

					return accumulator;
				}, {} as Record<string, React.ReactElement>),
		[modals, taxiServiceData],
	);

	const onClickTab = useCallback((key: TabData["id"]) => {
		// TODO:
	}, []);

	const dockLayoutLoadTab = useCallback(
		({ id }: TabBase) => {
			if (!id) return defaultTab;

			const tab = defaultTabs[id] as TabData;

			const retval = isUndefined(tab)
				? defaultTab
				: {
						...tab,
						...(isString(tab.title)
							? {
									title: (
										<StyledRow
											ref={
												tab.id === "chat"
													? refTabItem
													: undefined
											}
											id={`main_page_${tab.id}_widget_id`}
											alignItems="center"
											onClick={() => onClickTab(tab.id)}
										>
											<span>{t(tab.title)}</span>
										</StyledRow>
									),
							  }
							: {}),
						content: contents[id],
				  };

			return retval;
		},
		[contents, onClickTab, t],
	);

	const dockLayoutOnLayoutChange = useCallback(
		(newLayout: LayoutBase) => {
			pendingLayout.current = newLayout;
			if (layoutPersistTimer.current) {
				clearTimeout(layoutPersistTimer.current);
			}
			layoutPersistTimer.current = setTimeout(() => {
				if (pendingLayout.current) {
					dispatch(widgets.actions.setLayout(pendingLayout.current));
					pendingLayout.current = null;
				}
				layoutPersistTimer.current = null;
			}, 300);
		},
		[dispatch],
	);

	useEffect(
		() => () => {
			if (layoutPersistTimer.current) {
				clearTimeout(layoutPersistTimer.current);
			}
			if (pendingLayout.current) {
				dispatch(widgets.actions.setLayout(pendingLayout.current));
			}
		},
		[dispatch],
	);

	useEffect(() => {
		const timer = setTimeout(() => {
			const elem = refTabItem.current;
			const value = lastRead?.remaining || 0;

			if (value && elem) {
				elem.classList.add("new_message");
			} else if (elem && !value) {
				elem.classList.remove("new_message");
			}
		}, 0);

		return () => clearTimeout(timer);
	}, [lastRead]);

	useEffect(() => {
		if (!dockLayoutRef.current) return;

		const dockLayout = dockLayoutRef.current;

		Object.entries(contents).forEach(([id]) => {
			dockLayout.updateTab(id, { id } as TabData, false);
		});

		dockLayout.panelToFocus = null as unknown as string;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t, settingsLanguage, contents]);

	return (
		<Root>
			<DockLayout
				ref={dockLayoutRef}
				dropMode="edge"
				loadTab={dockLayoutLoadTab}
				layout={savedLayout}
				groups={groups}
				onLayoutChange={dockLayoutOnLayoutChange}
			/>
			<PerfProfiler id="OrdersSubscribe">
				<SubscribeProvider />
			</PerfProfiler>
		</Root>
	);
};

export interface ParkingsFilter {
	taxiServiceIds: number[];
	companyIds: number[];
	taxiServiceIdsPosition: number[];
	statuses: string[];
	online?: boolean;
	isTwoOnlineStatus: boolean;
	isTwoWorkingStatus: boolean;
	workingStatus?: boolean;
	isYourOrderStatus: boolean;
}

export default memo(MainOrderPage);
